import { applyVertexPositions, cloneTopology } from '../core.js';
import { falloffWeights, mirrorPairs } from '../falloff.js';
import { triangleRenderability } from '../validation.js';

/**
 * Move geometric corners rather than render-vertex ids.
 *
 * Canonical topology is captured at render-vertex granularity: a hard edge
 * duplicates its endpoint vertices once per adjacent face, so a box carries 24
 * vertices at 8 positions. `applyVertexPositions` assigns each id
 * independently, so moving "one vertex" of such a group moves one of several
 * co-located duplicates and tears the mesh at that point. This operator
 * addresses the group — every member of a corner is written to ONE IDENTICAL
 * value, so a welded move stays welded and, incidentally, restores exact
 * position coincidence for the adjacency layer that depends on it.
 *
 * ATOMICITY. Nothing is written until the whole result is known to be valid.
 * The displacement is applied to a clone, every face incident to a moved corner
 * is checked for renderability there, and only then is the same change applied
 * to the real topology. A rejected move leaves canonical topology byte
 * identical.
 */

export const SOURCE_MESH_NOT_RENDERABLE = 'SOURCE_MESH_NOT_RENDERABLE';
export const MOVE_WOULD_CREATE_DEGENERATE_FACE = 'MOVE_WOULD_CREATE_DEGENERATE_FACE';

function faceRenderability(topology, face, positionById) {
  return triangleRenderability(face.vertexIds.map((id) => positionById.get(id)));
}

function positionsOf(topology) {
  return new Map(topology.vertices.map((vertex) => [vertex.id, vertex.position]));
}

/**
 * Refuse a mesh that is ALREADY broken, before anything derived is computed.
 *
 * This has to run before `computeGeometricAdjacency`, not after: adjacency
 * itself throws `FACE_NOT_RENDERABLE` on a mesh containing a degenerate face
 * (it walks every face to build regions), so a check placed after it could
 * never be reached and the caller would get adjacency's error instead of one
 * that says which faces are the problem. See the degenerate-face audit.
 */
export function assertSourceRenderable(topology, commandName) {
  const positionById = positionsOf(topology);
  const faceIds = [];
  for (const face of topology.faces) {
    if (!faceRenderability(topology, face, positionById).renderable) faceIds.push(face.id);
  }
  if (faceIds.length === 0) return;
  const error = new Error(
    `${commandName}: this mesh already contains ${faceIds.length} non-renderable face(s) `
    + `(${faceIds.slice(0, 8).join(', ')}${faceIds.length > 8 ? ', …' : ''}); `
    + 'repair it or restore a snapshot before editing',
  );
  error.code = SOURCE_MESH_NOT_RENDERABLE;
  error.faceIds = faceIds;
  throw error;
}

/** Reflect a DISPLACEMENT across an axis-aligned plane: only the axis component flips. */
function reflectDelta(delta, axisIndex) {
  const mirrored = [...delta];
  mirrored[axisIndex] = -mirrored[axisIndex];
  return mirrored;
}

/**
 * @param topology mutated in place, but only after the whole move validates
 * @param corners  computeGeometricAdjacency(topology).corners
 * @param graph    buildSculptGraph(topology, corners) — required only for falloff
 * @param cornerIds seed corners
 * @param delta    displacement in the topology's own (object-local) space
 */
export function moveCorners(topology, {
  corners,
  graph = null,
  cornerIds,
  delta,
  mirror = null,
  falloff = null,
}) {
  const byId = new Map(corners.map((corner) => [corner.id, corner]));
  for (const id of cornerIds) {
    if (!byId.has(id)) throw new Error(`moveCorners: unknown corner id '${id}'`);
  }
  if (cornerIds.length === 0) throw new Error('moveCorners requires at least one corner id');

  // 1. Base displacement: the seeds, or the falloff neighbourhood around them.
  const displacement = new Map();
  let falloffAffectedCornerIds = [];
  if (falloff) {
    if (!graph) throw new Error('moveCorners: falloff requires a sculpt graph');
    const weights = falloffWeights(graph, cornerIds, falloff);
    for (const [cornerId, weight] of weights) {
      displacement.set(cornerId, delta.map((component) => component * weight));
    }
    falloffAffectedCornerIds = [...weights.keys()].filter((id) => !cornerIds.includes(id)).sort();
  } else {
    for (const cornerId of cornerIds) displacement.set(cornerId, [...delta]);
  }

  // 2. Mirror. Computed AFTER falloff and FROM it, so a mirrored corner receives
  //    its partner's actual displacement rather than an independently computed
  //    one — which is what keeps a symmetric edit exactly symmetric instead of
  //    merely approximately so.
  //
  //    A directly displaced corner keeps its own displacement: without that
  //    rule, selecting both halves of a symmetric mesh and asking for mirror
  //    would have each corner both move and be overwritten by its partner's
  //    reflection, and which won would depend on iteration order.
  let mirroredCornerIds = [];
  let unmatchedMirrorCornerIds = [];
  let ambiguousMirrorCornerIds = [];
  if (mirror) {
    const axisIndex = { x: 0, y: 1, z: 2 }[mirror.axis];
    const { pairs, unmatched, ambiguous } = mirrorPairs(corners, mirror);
    const unmatchedSet = new Set(unmatched);
    const ambiguousSet = new Set(ambiguous);
    const displacedIds = new Set(displacement.keys());
    const mirroredIds = [];
    const missing = [];
    const unclear = [];
    for (const cornerId of displacedIds) {
      const partner = pairs.get(cornerId);
      if (partner === undefined) {
        // Only report a seed — a falloff neighbour without a partner is not
        // something the caller asked for by name. Ambiguous is reported apart
        // from unmatched because they mean different things: no partner at all
        // versus several, none of which can be chosen without inventing an
        // answer.
        if (unmatchedSet.has(cornerId)) missing.push(cornerId);
        else if (ambiguousSet.has(cornerId)) unclear.push(cornerId);
        continue;
      }
      if (displacedIds.has(partner)) continue;
      displacement.set(partner, reflectDelta(displacement.get(cornerId), axisIndex));
      mirroredIds.push(partner);
    }
    mirroredCornerIds = mirroredIds.sort();
    unmatchedMirrorCornerIds = missing.sort();
    ambiguousMirrorCornerIds = unclear.sort();
  }

  // 3. Resolve to per-vertex absolute positions. Every member of a corner is
  //    written to the same value, computed once from the corner's position.
  const changes = [];
  for (const [cornerId, offset] of displacement) {
    const corner = byId.get(cornerId);
    const moved = corner.position.map((component, index) => component + offset[index]);
    for (const vertexId of corner.vertexIds) changes.push([vertexId, [...moved]]);
  }

  // 4. Validate on a CANDIDATE, never on the live topology.
  const candidate = cloneTopology(topology);
  applyVertexPositions(candidate, changes);
  const movedVertexIds = new Set(changes.map(([vertexId]) => vertexId));
  const candidatePositions = positionsOf(candidate);
  const brokenFaceIds = [];
  for (const face of candidate.faces) {
    if (!face.vertexIds.some((id) => movedVertexIds.has(id))) continue;
    if (!faceRenderability(candidate, face, candidatePositions).renderable) brokenFaceIds.push(face.id);
  }
  if (brokenFaceIds.length > 0) {
    const error = new Error(
      `moveCorners would make ${brokenFaceIds.length} face(s) non-renderable `
      + `(${brokenFaceIds.slice(0, 8).join(', ')}${brokenFaceIds.length > 8 ? ', …' : ''}); `
      + 'the move was rejected and the mesh is unchanged',
    );
    error.code = MOVE_WOULD_CREATE_DEGENERATE_FACE;
    error.faceIds = brokenFaceIds;
    error.cornerIds = [...displacement.keys()].sort();
    throw error;
  }

  // 5. Commit. Same changes, now known good.
  applyVertexPositions(topology, changes);

  return {
    movedCornerIds: [...displacement.keys()].sort(),
    movedVertexCount: movedVertexIds.size,
    mirroredCornerIds,
    unmatchedMirrorCornerIds,
    ambiguousMirrorCornerIds,
    falloffAffectedCornerIds,
  };
}
